import { AnalyticEvent, isNative, match, usePostHog } from "@stepapp/core";
import {
  AttributeType,
  NumericType,
  type OrderAttributePayload,
  V2CartQuestionsUpsert,
} from "@stepapp/core/apiTypes";
import { Block, Image, palette, spacing, Touchable, Typography } from "@stepapp/ui";
import React, { useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { broom, packageBox, roller } from "../../assets/images";
import OptionRibbon from "./OptionRibbon";
import QuantityActions from "./QuantityActions";

type Params = {
  id: string;
  title?: Maybe<string>;
  description?: Maybe<string>;
  selected?: boolean;
  questionId: string;
  questionType?: AttributeType;
  setNewAttribute?: (attributes: (OrderAttributePayload | V2CartQuestionsUpsert)[]) => void;
  setSubscriptionId?: (id: string) => void;
  icon?: string;
  discount?: number;
  isFeatured?: boolean;
  attributes?: (OrderAttributePayload | V2CartQuestionsUpsert)[];
  isPeriodicityQuestion?: boolean;
  numericType?: NumericType | null;
};

const iconMapping = {
  broom,
  roller,
  package: packageBox,
};

const SimpleAnswerItem = ({
  attributes,
  discount = 0,
  icon,
  questionId,
  questionType,
  setNewAttribute,
  setSubscriptionId,
  title = "",
  description,
  selected,
  id,
  isFeatured,
  isPeriodicityQuestion = false,
  numericType,
}: Params) => {
  const postHog = usePostHog();

  const isNumericQuestion = questionType === AttributeType.Numeric;

  const currentAnswer = attributes?.find((a) => a?.valueId?.includes(id));

  const handleItemSelect = useCallback(() => {
    const filteredQuestions =
      attributes?.filter((attribute) => attribute?.attributeId !== questionId) || [];
    const filteredAnswers = attributes?.filter((a) => !a?.valueId?.includes(id)) || [];

    const newAttribute = { attributeId: questionId, valueId: [id], quantity: 1 };

    setNewAttribute?.(
      match({ questionType, selected })
        .with({ questionType: AttributeType.SingleChoice, selected: true }, () => filteredQuestions)
        .with({ questionType: AttributeType.SingleChoice, selected: false }, () => [
          ...filteredQuestions,
          newAttribute,
        ])
        .with({ questionType: AttributeType.MultipleChoice, selected: true }, () => filteredAnswers)
        .with({ questionType: AttributeType.MultipleChoice, selected: false }, () => [
          ...(attributes || []),
          newAttribute,
        ])
        .otherwise(() => []),
    );
  }, [attributes, selected, id, questionType, questionId, setNewAttribute]);

  const handleSelectQuantity = useCallback(
    (quantity: number) => {
      const filteredAnswers = attributes?.filter((a) => !a?.valueId?.includes(id)) || [];

      match(quantity)
        .with(0, () => setNewAttribute?.(filteredAnswers))
        .otherwise(() => {
          setNewAttribute?.(
            filteredAnswers.concat([{ attributeId: questionId, valueId: [id], quantity }]),
          );
        });
    },
    [id, attributes, questionId, setNewAttribute],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleSelect = useCallback(() => {
    if (isPeriodicityQuestion && setSubscriptionId) {
      setSubscriptionId(id);

      postHog?.capture(AnalyticEvent.Reservation.Periodicity, { discount, title });
    } else {
      handleItemSelect();
    }
  }, [handleItemSelect, setSubscriptionId, id, isPeriodicityQuestion, discount, title]);

  const iconSource = iconMapping[icon as "package"];

  const Wrapper = isNumericQuestion ? View : Touchable;

  return (
    <Wrapper onPress={handleSelect} testID={`answer-id-${id}`}>
      <Block
        justifyCenter
        marginVertical={isNative ? 1 : 0}
        paddingHorizontal={1.5}
        style={[
          styles.content,
          selected && (isNative ? styles.selectedWithoutShadow : styles.selected),
        ]}
      >
        <Block alignCenter row>
          {iconSource && (
            <Block height={4} marginRight={2} width={5}>
              <Image height="100%" source={iconSource} width="80%" />
            </Block>
          )}
          <Block flex={1} justifyCenter>
            <Typography type={selected ? "white" : "primary"} variant="button">
              {title}
            </Typography>

            {!!description && (
              <Block>
                <Typography type={selected ? "white" : "secondary"} variant="lightLabel">
                  {description}
                </Typography>
              </Block>
            )}
          </Block>

          {isNumericQuestion && (
            <QuantityActions
              onChange={handleSelectQuantity}
              quantity={currentAnswer?.quantity || 0}
              singleOption={true}
              numericType={numericType}
              textColor="white"
            />
          )}
        </Block>
        {isFeatured && <OptionRibbon />}
      </Block>
    </Wrapper>
  );
};

export default React.memo(SimpleAnswerItem);

const styles = StyleSheet.create({
  content: {
    backgroundColor: palette.common.lightGray,
    borderRadius: spacing.radius,
    minHeight: spacing.unit * 9,
    overflow: "hidden",
  },
  selected: {
    backgroundColor: palette.primary.accent,
    boxShadow:
      "0px 100px 80px rgba(0, 0, 84, 0.07), 0px 41.7776px 33.4221px rgba(0, 0, 84, 0.0503198), 0px 22.3363px 17.869px rgba(0, 0, 84, 0.0417275), 0px 12.5216px 10.0172px rgba(0, 0, 84, 0.035), 0px 6.6501px 5.32008px rgba(0, 0, 84, 0.0282725), 0px 2.76726px 2.21381px rgba(0, 0, 84, 0.0196802)",
  },
  selectedWithoutShadow: {
    backgroundColor: palette.primary.accent,
  },
});
